import React from "react";
import "./AQWpackages.css";

const MassBalanceAnimation = () => {
  return (
    <section style={{ padding: "72px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--accent)",
              margin: "0 0 10px"
            }}
          >
            MASS BALANCE
          </p>
          <h2
            style={{
              fontSize: "clamp(28px, 4vw, 44px)",
              margin: "0 0 14px",
              fontWeight: 400
            }}
          >
            Real-time mass balance tracking
          </h2>
          <p style={{ color: "var(--muted)", fontSize: 16, margin: 0 }}>
            Watch how our system monitors your farm's complete nutritional cycle
          </p>
        </div>
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(79,142,247,0.08) 0%, rgba(162,89,255,0.08) 100%), linear-gradient(var(--surface), var(--surface))",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 24,
            padding: 40,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            minHeight: 500,
            position: "relative",
            overflow: "hidden"
          }}
        >
          <p
            style={{
              textAlign: "center",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--accent)",
              margin: "24px 0 0",
              zIndex: 1
            }}
          >
            Mass Balance - Carbon
          </p>

          {/* Below 640px the SVG's fixed height squeezes the whole 1080x500 diagram into a
              sliver and the text becomes unreadable, so it is replaced by a simple stacked
              flow instead of trying to scale it down. */}
          <style>{`
            .mb-svg-wrap { width: 100%; z-index: 1; }
            .mb-mobile-flow { display: none; }
            @media (max-width: 640px) {
              .mb-svg-wrap { display: none; }
              .mb-mobile-flow { display: flex; flex-direction: column; gap: 10px; width: 100%; max-width: 340px; margin: 0 auto; z-index: 1; }
              .mb-intro { margin: 0 0 4px; font-size: 12px; color: var(--muted); text-align: center; line-height: 1.5; }
              .mb-card { background: var(--surface); border: 1.5px solid var(--mc); border-radius: 14px; padding: 12px 14px; text-align: center; }
              .mb-value { margin: 0 0 4px; font-size: 16px; font-weight: 700; color: var(--mc); }
              .mb-name { margin: 0; font-size: 13px; font-weight: 600; color: var(--text); }
              .mb-sub { margin: 2px 0 6px; font-size: 10px; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; }
              .mb-desc { margin: 0; font-size: 11.5px; color: var(--muted); line-height: 1.4; }
              .mb-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
              .mb-row .mb-desc { font-size: 11px; }
              .mb-arrow { display: flex; flex-direction: column; align-items: center; color: var(--muted); opacity: .8; margin: -2px 0; gap: 2px; }
              .mb-arrow svg { width: 16px; height: 16px; }
              .mb-arrow-label { font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
            }
          `}</style>
          <div className="mb-mobile-flow">
            <p className="mb-intro">How each kg of feed carbon moves through the tank:</p>
            <div className="mb-card" style={{ '--mc': '#FFAD60' } as React.CSSProperties}>
              <p className="mb-value">1 kg</p>
              <p className="mb-name">Feed Input</p>
              <p className="mb-sub">TOC</p>
              <p className="mb-desc">Feed carbon goes into the tank</p>
            </div>
            <div className="mb-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "rotate(90deg)" }}>
                <path d="M5 12h13" /><path d="M13 6l6 6-6 6" />
              </svg>
            </div>
            <div className="mb-card" style={{ '--mc': '#9ED3CD' } as React.CSSProperties}>
              <p className="mb-name">Fish Growth</p>
              <p className="mb-sub">TOC</p>
              <p className="mb-desc">The fish eat the feed and grow, storing most of that carbon as biomass</p>
            </div>
            <div className="mb-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "rotate(90deg)" }}>
                <path d="M5 12h13" /><path d="M13 6l6 6-6 6" />
              </svg>
              <span className="mb-arrow-label">the rest leaves as</span>
            </div>
            <div className="mb-row">
              <div className="mb-card" style={{ '--mc': '#4F8EF7' } as React.CSSProperties}>
                <p className="mb-value">&#8776; 0.5 kg</p>
                <p className="mb-name">CO₂ - Respiration</p>
                <p className="mb-sub">TIC</p>
                <p className="mb-desc">Carbon the fish breathe out, dissolved in the water</p>
              </div>
              <div className="mb-card" style={{ '--mc': '#A259FF' } as React.CSSProperties}>
                <p className="mb-name">Waste Output</p>
                <p className="mb-sub">TOC</p>
                <p className="mb-desc">Uneaten feed and faeces, removed as solid waste</p>
              </div>
            </div>
            <div className="mb-arrow" style={{ alignItems: "flex-start", paddingLeft: "25%" }} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "rotate(90deg)" }}>
                <path d="M5 12h13" /><path d="M13 6l6 6-6 6" />
              </svg>
            </div>
            <div className="mb-card" style={{ '--mc': '#4F8EF7' } as React.CSSProperties}>
              <p className="mb-name">CO₂ - Degassing</p>
              <p className="mb-sub">TIC</p>
              <p className="mb-desc">That dissolved CO₂ is stripped back out of the water and vented to air</p>
            </div>
          </div>

          <div className="mb-svg-wrap">
          <svg
            width="100%"
            height={500}
            viewBox="-200 0 1080 500"
            style={{ display: "block", maxWidth: 1080, width: "100%", margin: "0 auto", zIndex: 1 }}
          >
            <defs>
              <style
                dangerouslySetInnerHTML={{
                  __html:
                    "\n          @keyframes feedFall {\n            0% { opacity: 0; transform: translate(0, 0); }\n            10% { opacity: 1; }\n            90% { opacity: 1; }\n            100% { opacity: 0; transform: translate(300px, -20px); }\n          }\n          @keyframes fishBreathe {\n            0%, 100% { transform: translateY(0px); }\n            50% { transform: translateY(30px); }\n          }\n          @keyframes wasteFall {\n            0% { opacity: 0; transform: translate(0, 0); }\n            10% { opacity: 1; }\n            90% { opacity: 1; }\n            100% { opacity: 0; transform: translate(245px, 100px); }\n          }\n          @keyframes bubbleRise {\n            0% { opacity: 0; offset-distance: 0%; }\n            10% { opacity: 1; }\n            90% { opacity: 1; }\n            100% { opacity: 0; offset-distance: 100%; }\n          }\n          .feed { animation: feedFall 2s ease-in infinite; }\n          .fish { animation: fishBreathe 3s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }\n          .waste { animation: wasteFall 2s ease-out infinite; }\n          .bubble { animation: bubbleRise 2.5s linear infinite; }\n         "
                }}
              />
            </defs>

            {/* Tank containing the fish */}
            <rect x={0} y={60} width={600} height={320} rx={16} fill="rgba(158,211,205,0.05)" stroke="#9ED3CD" strokeWidth={2} strokeOpacity={0.5} />

            {/* Recirculation pipes, top-right of the tank: out on top, back in on the bottom */}
            <path d="M 600 95 L 660 95 L 660 175 L 600 175 L 600 157 L 642 157 L 642 113 L 600 113 Z" fill="#4F8EF7" fillOpacity={0.5} stroke="#4F8EF7" strokeWidth={1} />
            <polygon points="614,98 614,110 629,104" fill="#9ED3CD" />
            <polygon points="629,160 629,172 614,166" fill="#9ED3CD" />
            {/* CO2 escaping out of the pipe, to the east */}
            <line x1={660} y1={135} x2={669} y2={135} stroke="#9eb3c8" strokeWidth={2} />
            <polygon points="669,129 669,141 681,135" fill="#9eb3c8" />

            {/* CO2 Degassing label */}
            <g>
              <rect x={685} y={108} width={170} height={54} rx={16} fill="rgba(79,142,247,0.15)" stroke="rgba(79,142,247,0.4)" strokeWidth={1} />
              <text x={770} y={127} fontSize={14} fill="#4F8EF7" textAnchor="middle" fontWeight={600}>CO₂ - Degassing<tspan x={770} dy={20}>(TIC)</tspan></text>
            </g>

            {/* Feed input from top left */}
            <g className="feed" style={{ animationDelay: "0s" }}>
              <circle cx={-25} cy={215} r={8} fill="rgba(255,173,96,0.6)" />
            </g>
            <g className="feed" style={{ animationDelay: "0.7s" }}>
              <circle cx={-10} cy={215} r={6} fill="rgba(255,173,96,0.5)" />
            </g>
            <g className="feed" style={{ animationDelay: "1.4s" }}>
              <circle cx={-35} cy={215} r={7} fill="rgba(255,173,96,0.55)" />
            </g>

            {/* Fish icon (inlined Lucide "fish" path, no external dependency).
                Positioning/scaling/mirroring lives on this plain SVG transform
                attribute (not CSS), so it never conflicts with the .fish
                CSS animation's own `transform` on the parent group below. */}
            <g className="fish">
              <g transform="translate(270,170) scale(2.5,2.5) translate(24,0) scale(-1,1)"
                 fill="none" stroke="#9ED3CD" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.47-3.44 6-7 6s-7.56-2.53-8.5-6Z" />
                <path d="M18 12v.5" />
                <path d="M16 17.93a9.77 9.77 0 0 1 0-11.86" />
                <path d="M7 10.67C7 8 5.58 5.97 2.73 5.5c-1 1.5-1 5 .23 6.5-1.24 1.5-1.24 5-.23 6.5C5.58 18.03 7 16 7 13.33" />
                <path d="M10.46 7.26C10.2 5.88 9.17 4.24 8 3h5.8a2 2 0 0 1 1.98 1.67l.23 1.4" />
                <path d="m16.01 17.93-.23 1.4A2 2 0 0 1 13.8 21H9.5a5.96 5.96 0 0 0 1.49-3.98" />
              </g>
            </g>

            {/* Waste output from fish downward */}
            <g className="waste" style={{ animationDelay: "0.5s" }}>
              <circle cx={360} cy={225} r={7} fill="rgba(162,89,255,0.5)" />
            </g>
            <g className="waste" style={{ animationDelay: "1.2s" }}>
              <circle cx={375} cy={225} r={6} fill="rgba(162,89,255,0.4)" />
            </g>
            <g className="waste" style={{ animationDelay: "1.9s" }}>
              <circle cx={370} cy={225} r={5} fill="rgba(162,89,255,0.45)" />
            </g>

            {/* Bubbles rising from fish area, arcing around the CO2-Respiration dashed trajectory */}
            <g className="bubble" style={{ animationDelay: "0s", offsetPath: "path('M 320 180 Q 388 158 415 102')" }}>
              <circle cx={0} cy={0} r={5} fill="rgba(79,142,247,0.6)" />
            </g>
            <g className="bubble" style={{ animationDelay: "0.6s", offsetPath: "path('M 330 178 Q 378 142 427 100')" }}>
              <circle cx={0} cy={0} r={4} fill="rgba(79,142,247,0.5)" />
            </g>
            <g className="bubble" style={{ animationDelay: "1.2s", offsetPath: "path('M 325 183 Q 393 163 420 105')" }}>
              <circle cx={0} cy={0} r="4.5" fill="rgba(79,142,247,0.55)" />
            </g>

            {/* Labels */}
            <g>
              <text x={-100} y={178} fontSize={16} fill="#FFAD60" textAnchor="middle" fontWeight={700}>1 kg</text>
              <rect x={-170} y={193} width={140} height={54} rx={16} fill="rgba(255,173,96,0.15)" stroke="rgba(255,173,96,0.4)" strokeWidth={1} />
              <text x={-100} y={212} fontSize={14} fill="#FFAD60" textAnchor="middle" fontWeight={600}>Feed Input<tspan x={-100} dy={20}>(TOC)</tspan></text>
            </g>
            <g>
              <rect x={220} y={258} width={160} height={54} rx={16} fill="rgba(158,211,205,0.15)" stroke="rgba(158,211,205,0.4)" strokeWidth={1} />
              <text x={300} y={277} fontSize={14} fill="#9ED3CD" textAnchor="middle" fontWeight={600}>Fish Growth<tspan x={300} dy={20}>(TOC)</tspan></text>
            </g>

            {/* Waste outlet pipe, right side of the tank */}
            <rect x={600} y={317} width={55} height={18} fill="#A259FF" fillOpacity={0.5} stroke="#A259FF" strokeWidth={1} />
            <polygon points="640,320 640,332 655,326" fill="#A259FF" />

            <g>
              <rect x={670} y={299} width={170} height={54} rx={16} fill="rgba(162,89,255,0.15)" stroke="rgba(162,89,255,0.4)" strokeWidth={1} />
              <text x={755} y={318} fontSize={14} fill="#A259FF" textAnchor="middle" fontWeight={600}>Waste Output<tspan x={755} dy={20}>(TOC)</tspan></text>
            </g>

            <g>
              <text x={505} y={90} fontSize={16} fill="#4F8EF7" textAnchor="middle" fontWeight={700}>&#8776; 0.5 kg</text>
              <rect x={425} y={105} width={160} height={54} rx={16} fill="rgba(79,142,247,0.15)" stroke="rgba(79,142,247,0.4)" strokeWidth={1} />
              <text x={505} y={124} fontSize={14} fill="#4F8EF7" textAnchor="middle" fontWeight={600}>CO₂ - Respiration<tspan x={505} dy={20}>(TIC)</tspan></text>
            </g>

            {/* Vertical equilibrium arrows + bicarbonate/carbonate formula, below the CO2 - Respiration label */}
            <g>
              <line x1={499} y1={193} x2={499} y2={175} stroke="#4F8EF7" strokeWidth={2} />
              <polygon points="493,175 505,175 499,167" fill="#4F8EF7" />
              <line x1={511} y1={167} x2={511} y2={185} stroke="#4F8EF7" strokeWidth={2} />
              <polygon points="505,185 517,185 511,193" fill="#4F8EF7" />
              <text x={505} y={216} fontSize={14} fill="#4F8EF7" textAnchor="middle" fontWeight={600}>
                <tspan>HCO</tspan>
                <tspan dy={4} fontSize={10}>3</tspan>
                <tspan dy={-7} fontSize={10}>-</tspan>
                <tspan dy={3}> + CO</tspan>
                <tspan dy={4} fontSize={10}>3</tspan>
                <tspan dy={-7} fontSize={10}>2-</tspan>
              </text>
            </g>

            {/* Flow arrows */}
            <path d="M -10 213 Q 100 175 270 195" stroke="#FFAD60" strokeWidth={2} fill="none" strokeDasharray="10,8" />
            <path d="M 350 225 Q 480 290 595 325" stroke="rgba(162,89,255,0.3)" strokeWidth={2} fill="none" strokeDasharray="10,8" />
            <path d="M 325 175 Q 365 125 420 97" stroke="rgba(79,142,247,0.4)" strokeWidth={2} fill="none" strokeDasharray="10,8" />
          </svg>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 24,
            width: "100%",
            marginTop: 32
          }}
        >
          <div
            style={{
              background: "linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.06)), var(--surface)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 16,
              padding: 20,
              backdropFilter: "blur(10px)"
            }}
          >
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--muted)",
                margin: "0 0 16px"
              }}
            >
              Carbon (C)
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                marginBottom: 16
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#FFAD60",
                  marginTop: 6,
                  flexShrink: 0
                }}
              />
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--muted)", textAlign: "left" }}>
                  Feed input - Biomass - Waste - CO₂ emissions
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#FFAD60",
                    textAlign: "center"
                  }}
                >
                  4.6 g/day
                </p>
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.1)",
                paddingTop: 12,
                fontSize: 12
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6
                }}
              >
                <span style={{ color: "var(--muted)" }}>Total year:</span>{" "}
                <span style={{ color: "#FFAD60", fontWeight: 600 }}>1.7 kg/tonn biomass</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--muted)" }}>Annual Limit:</span>{" "}
                <span style={{ color: "#9eb3c8", fontWeight: 600 }}>32 kg/tonn biomass</span>
              </div>
            </div>
          </div>
          <div
            style={{
              background: "linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.06)), var(--surface)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 16,
              padding: 20,
              backdropFilter: "blur(10px)"
            }}
          >
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--muted)",
                margin: "0 0 16px"
              }}
            >
              Phosphorus (P)
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                marginBottom: 16
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#A259FF",
                  marginTop: 6,
                  flexShrink: 0
                }}
              />
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--muted)", textAlign: "left" }}>
                  Waste output
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#A259FF",
                    textAlign: "center"
                  }}
                >
                  0.18 g/day
                </p>
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.1)",
                paddingTop: 12,
                fontSize: 12
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6
                }}
              >
                <span style={{ color: "var(--muted)" }}>Total Year:</span>{" "}
                <span style={{ color: "#A259FF", fontWeight: 600 }}>2.9 kg/tonn biomass</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--muted)" }}>Anual Limit:</span>{" "}
                <span style={{ color: "#9eb3c8", fontWeight: 600 }}>3.6 kg/tonn biomass</span>
              </div>
            </div>
          </div>
          <div
            style={{
              background: "linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.06)), var(--surface)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 16,
              padding: 20,
              backdropFilter: "blur(10px)"
            }}
          >
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--muted)",
                margin: "0 0 16px"
              }}
            >
              Nitrogen (N)
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                marginBottom: 16
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#4F8EF7",
                  marginTop: 6,
                  flexShrink: 0
                }}
              />
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--muted)", textAlign: "left" }}>
                  Feed input - Biomass - Excretion - Waste
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#4F8EF7",
                    textAlign: "center"
                  }}
                >
                  1.8 g/day
                </p>
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.1)",
                paddingTop: 12,
                fontSize: 12
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6
                }}
              >
                <span style={{ color: "var(--muted)" }}>Total Year:</span>{" "}
                <span style={{ color: "#4F8EF7", fontWeight: 600 }}>36.3 Kg/tonn biomass</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--muted)" }}>Anual Limit:</span>{" "}
                <span style={{ color: "#9eb3c8", fontWeight: 600 }}>37 kg/tonn biomass</span>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            background: "linear-gradient(rgba(79,142,247,0.08), rgba(79,142,247,0.08)), var(--surface)",
            border: "1px solid rgba(79,142,247,0.2)",
            borderRadius: 16,
            padding: 16,
            backdropFilter: "blur(10px)",
            width: "100%",
            marginTop: 24
          }}
        >
          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#4F8EF7",
              margin: "0 0 8px"
            }}
          >
            Mass Balance Status
          </p>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: "var(--muted)",
              lineHeight: "1.5"
            }}
          >
            System is operating at optimal nutrient conversion. Feed input balanced
            with fish growth.
          </p>
        </div>
      </div>
    </section>
  );
};

export default MassBalanceAnimation;
